import { Flex } from "antd";
import { HoverableCard } from "components/HoverableCard";
import { forwardRef, useMemo } from "react";
import { Header } from "./Preview/components/Header";
import { Logo } from "./Preview/components/Logo";
import { Presenter } from "./Preview/components/Presenter";
import { Text } from "./Preview/components/Text";
import { TemplateImages } from "./Preview/components/TemplateImages";
import { TemplateVideos } from "./Preview/components/TemplateVideos";
import { TemplateCustomTexts } from "./Preview/components/TemplateCustomTexts";
import { ImageVideoPreview } from "../common/ImageVideoPreview";
import { BodyView, Chapter, ITEM_TYPES, Size, TemplateType } from "../../types";
import { ColoredBackground } from "./Preview/components/ColoredBackground";
import { TemplateShapes } from "./Preview/components/TemplateShapes";
import { useTemplateEditorStore } from "../../store/useTemplateEditorStore";
import { LAYOUT_PREVIEW_OUTLINE_WIDTH } from "../../constants";
import { StyledPreview } from "./SlideEditableTemplate";
import { radius } from "@libertify/ui";

export type TemplatePreviewProps = {
  chapter: Chapter;
  previewText?: string;
  onCardClick: () => void;
  border: {
    borderWidth?: number;
    borderColor?: string;
    borderRadius?: number;
  };
  borderColor?: string;
  opacity?: number;
  templateSize: Size;
};

export const TemplatePreview = forwardRef<HTMLDivElement, TemplatePreviewProps>(
  (
    {
      chapter,
      previewText,
      onCardClick,
      border: {
        borderWidth = LAYOUT_PREVIEW_OUTLINE_WIDTH,
        borderColor,
        borderRadius = radius.box,
      },
      opacity = 0.3,
      templateSize,
    },
    ref
  ) => {
    const { templateId, type: templateType } = chapter;

    const {
      templates,
      defaultChapter,
      defaultTransition,
      defaultIntro,
      defaultClosing,
    } = useTemplateEditorStore((store) => ({
      templates: store.templates,
      defaultChapter: store.defaultChapter,
      defaultTransition: store.defaultTransition,
      defaultIntro: store.defaultIntro,
      defaultClosing: store.defaultClosing,
    }));

    const { width, height } = templateSize;

    const template = useMemo(() => {
      const savedTemplate = templateId && templates.get(templateId);
      if (savedTemplate) {
        return savedTemplate;
      }
      if (templateType === TemplateType.TRANSITION) {
        return defaultTransition;
      }
      if (templateType === TemplateType.INTRODUCTION) {
        return defaultIntro;
      }
      if (templateType === TemplateType.CLOSING) {
        return defaultClosing;
      }
      return defaultChapter;
    }, [
      defaultChapter,
      defaultClosing,
      defaultIntro,
      defaultTransition,
      templateId,
      templateType,
      templates,
    ]);

    const backgroundItem:
      | {
          view: BodyView;
          assetId?: string;
          assetPath?: string;
        }
      | undefined = useMemo(() => {
      if (chapter.type === TemplateType.CHAPTER) {
        if (template.background.bodyView === "Video") {
          return {
            view: "Video",
            assetId: template.background.bodyVideoAsset,
            assetPath: template.background.bodyVideo,
          };
        } else if (template.background.bodyView === "Picture") {
          return {
            view: "Picture",
            assetId: template.background.bodyImageAsset,
            assetPath: template.background.bodyImage,
          };
        } else {
          return undefined;
        }
      }
      if (chapter.type === TemplateType.INTRODUCTION && template.intro.enable) {
        return {
          view: "Video",
          assetId: template.intro.assetId,
          assetPath: template.intro.assetPath,
        };
      }
      if (chapter.type === TemplateType.CLOSING && template.closing.enable) {
        return {
          view: "Video",
          assetId: template.closing.assetId,
          assetPath: template.closing.assetPath,
        };
      }
      if (
        chapter.type === TemplateType.TRANSITION &&
        template.transition.view !== "Color"
      ) {
        return {
          view: template.transition.view,
          assetId: template.transition.assetId,
          assetPath: template.transition.assetPath,
        };
      }
    }, [chapter, template]);

    return (
      <Flex vertical gap={10} style={{ position: "relative" }}>
        <HoverableCard
          ref={ref}
          data-interactive
          data-testid="hoverable-card"
          hoverable
          styles={{
            body: {
              padding: 0,
              overflow: "hidden",
              // Clamp at 0: a negative radius is invalid CSS and gets ignored,
              // which lets antd Card's default (rounded bottom corners) show.
              borderRadius: Math.max(0, borderRadius - borderWidth),
              width: "fit-content",
            },
          }}
          opacity={opacity}
          style={{
            borderRadius,
            borderWidth,
            borderColor,
            width: "fit-content",
            margin: "auto",
          }}
          onClick={onCardClick}
        >
          <StyledPreview width={width} height={height}>
            {backgroundItem?.assetId && (
              <ImageVideoPreview
                type={
                  backgroundItem.view === "Video"
                    ? ITEM_TYPES.Video
                    : ITEM_TYPES.Image
                }
                assetId={backgroundItem.assetId}
                path={backgroundItem.assetPath}
              />
            )}
            {template.background.bodyColor && (
              <ColoredBackground
                width={width}
                height={height}
                backgroundColor={template.background.bodyColor}
                disableOnclick
              />
            )}
            <TemplateImages
              templateImages={template.images}
              previewWidth={width}
              previewHeight={height}
            />
            <TemplateVideos
              templateVideos={template.videos}
              previewWidth={width}
              previewHeight={height}
            />
            <TemplateCustomTexts
              previewWidth={width}
              previewHeight={height}
              customTexts={template.customTexts}
            />
            <TemplateShapes
              shapes={template.shapes}
              previewWidth={width}
              previewHeight={height}
            />
            {templateType === TemplateType.CHAPTER && (
              <>
                {template.logo.showLogo && template.logo.assetId && (
                  <>
                    <Header
                      headerItem={template.header}
                      previewWidth={width}
                      previewHeight={height}
                    />
                    <div
                      data-testid="logo"
                      style={{
                        position: "absolute",
                        zIndex: template.logo.layerIndex,
                        top: template.logo.position.y * height,
                        left: template.logo.position.x * width,
                        width: template.logo.size.width * width,
                        height: template.logo.size.height * height,
                        transformOrigin: "center",
                        transform: template.logo.rotation
                          ? `rotate(${template.logo.rotation}deg)`
                          : undefined,
                      }}
                    >
                      <Logo
                        logo={template.logo}
                        previewWidth={width}
                        previewHeight={height}
                      />
                    </div>
                  </>
                )}
                {template.textStyle.showSubtitles && (
                  <Text
                    textFragment={previewText}
                    textItem={template.textStyle}
                    styles={{
                      position: "absolute",
                      overflow: "hidden",
                      top: template.textStyle.position.y * height,
                      left: template.textStyle.position.x * width,
                      transformOrigin: "center",
                      transform: template.textStyle.rotation
                        ? `rotate(${template.textStyle.rotation}deg)`
                        : undefined,
                    }}
                    previewWidth={width}
                    previewHeight={height}
                  />
                )}
                {template.presenter.presenter && (
                  <Presenter
                    presenter={template.presenter}
                    previewWidth={width}
                    previewHeight={height}
                  />
                )}
              </>
            )}
            {templateType === TemplateType.TRANSITION && (
              <>
                {template.titleStyle.showTitle && (
                  <Text
                    textFragment={previewText}
                    textItem={template.titleStyle}
                    styles={{
                      position: "absolute",
                      overflow: "hidden",
                      top: template.titleStyle.position.y * height,
                      left: template.titleStyle.position.x * width,
                      transformOrigin: "center",
                      transform: template.titleStyle.rotation
                        ? `rotate(${template.titleStyle.rotation}deg)`
                        : undefined,
                    }}
                    previewWidth={width}
                    previewHeight={height}
                  />
                )}
                {template.presenter.presenter && (
                  <Presenter
                    presenter={template.presenter}
                    previewWidth={width}
                    previewHeight={height}
                  />
                )}
              </>
            )}
          </StyledPreview>
        </HoverableCard>
      </Flex>
    );
  }
);

TemplatePreview.displayName = "TemplatePreview";
